import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays,
  addMonths, subMonths, isSameDay, isSameMonth, isToday as isDateToday,
} from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { ScoreBadge } from './ScoreBadge';

// Calendario mensual de seguimientos — Commit 14 (3/3). Grid de semanas
// (grid-cols-7, cabeceras EEE, weeksStartOn locale-aware) mismo patrón
// que MonthView.js (Planner) — no se inventa una estructura nueva. La
// navegación prev/siguiente mes no tenía precedente que reutilizar
// (Planner navega por día, no por mes), así que es nueva pero usa los
// mismos iconos ChevronLeft/ChevronRight que el resto del módulo.
//
// Vive como sección dentro de LeadScannerPage.js (que ya tiene su fix
// de h-full overflow-y-auto en la raíz de la página, Commit 14 2/3) —
// no necesita su propio contenedor con overflow: es contenido normal
// que fluye dentro de esa página ya scrolleable, no una página propia.
export function LeadCalendar() {
  const { api, language } = useAuth();
  const navigate = useNavigate();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;
  const weekStartsOn = language === 'en' ? 0 : 1;

  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    setSelectedDay(null);
    try {
      const monthParam = format(month, 'yyyy-MM');
      const res = await api.get('/leads/calendar', { params: { month: monthParam } });
      setDays(res.data.days || {});
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
      setDays({});
    } finally {
      setLoading(false);
    }
  }, [api, month]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  const gridDays = useMemo(() => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const gridStart = startOfWeek(monthStart, { weekStartsOn });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn });
    const result = [];
    let d = gridStart;
    while (d <= gridEnd) { result.push(d); d = addDays(d, 1); }
    return result;
  }, [month, weekStartsOn]);

  const weeks = useMemo(() => {
    const w = [];
    for (let i = 0; i < gridDays.length; i += 7) w.push(gridDays.slice(i, i + 7));
    return w;
  }, [gridDays]);

  const selectedDayKey = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null;
  const selectedDayLeads = selectedDayKey ? days[selectedDayKey] || [] : [];

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-zinc-200 uppercase tracking-wide">
          {format(month, 'MMMM yyyy', { locale })}
        </h2>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setMonth((m) => subMonths(m, 1))} data-testid="calendar-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setMonth((m) => addMonths(m, 1))} data-testid="calendar-next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 mb-1">
            {(weeks[0] || []).map((day) => (
              <div key={day.toISOString()} className="text-center text-[10px] text-zinc-500 uppercase tracking-wide py-1">
                {format(day, 'EEE', { locale })}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-px">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-px">
                {week.map((day) => {
                  const dayKey = format(day, 'yyyy-MM-dd');
                  const dayLeads = days[dayKey] || [];
                  const isCurrentMonth = isSameMonth(day, month);
                  const isSelected = selectedDay && isSameDay(day, selectedDay);

                  return (
                    <button
                      key={dayKey}
                      onClick={() => setSelectedDay(dayLeads.length > 0 ? day : null)}
                      disabled={dayLeads.length === 0}
                      className={`
                        relative flex flex-col items-center justify-start pt-1.5 pb-1 min-h-[48px] rounded-sm transition-colors
                        ${isCurrentMonth ? 'text-zinc-200' : 'text-zinc-600'}
                        ${isSelected ? 'bg-indigo-900/60 ring-1 ring-indigo-500' : dayLeads.length > 0 ? 'hover:bg-zinc-800 cursor-pointer' : 'cursor-default'}
                        ${isDateToday(day) && !isSelected ? 'ring-1 ring-zinc-600' : ''}
                      `}
                    >
                      <span className={`text-xs font-medium ${isDateToday(day) ? 'text-indigo-400' : ''}`}>
                        {format(day, 'd')}
                      </span>
                      {dayLeads.length > 0 && (
                        <span className={`mt-0.5 text-[9px] font-semibold px-1.5 rounded-full ${
                          isCurrentMonth ? 'bg-indigo-900/80 text-indigo-300' : 'bg-zinc-800 text-zinc-500'
                        }`}>
                          {dayLeads.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {selectedDay && (
            <div className="mt-4 border-t border-zinc-800 pt-3">
              <h3 className="text-xs font-semibold text-zinc-400 mb-2">
                {format(selectedDay, 'EEEE, d MMMM', { locale })}
              </h3>
              <div className="space-y-1.5">
                {selectedDayLeads.map((lead) => (
                  <button
                    key={lead.id}
                    onClick={() => navigate(`/internal/ai-lead-scanner/leads/${lead.id}`)}
                    className="w-full flex items-center justify-between bg-zinc-800/50 hover:bg-zinc-800 rounded-lg px-3 py-2 text-left transition-colors"
                    data-testid={`calendar-lead-${lead.id}`}
                  >
                    <span className="text-sm text-zinc-200">{lead.business_name}</span>
                    <ScoreBadge score={lead.score} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
